import CashStackIcon from 'assets/icons/cash-stack-icon.svg?component'
import ClockIcon from 'assets/icons/clock-icon.svg?component'
import PercentageIcon from 'assets/icons/percentage-icon.svg?component'
import AppStore from 'assets/icons/socials/app-store-success.svg?component'
import GooglePlay from 'assets/icons/socials/google-play-success.svg?component'
import StarIcon from 'assets/icons/star-icon.svg?component'
import IPImageDark from 'assets/images/landing/business_credit_night.png'
import IPImageLight from 'assets/images/landing/business_credit_optimized.png'
import ConditionBlock from 'components/UI/Blocks/ConditionBlock'
import { ConditionProps } from 'components/UI/Blocks/ConditionBlock'
import ContainerWithSteps from 'components/UI/Container/ContainerWithSteps'
import { Modal } from 'components/UI/Modal'
import Typography from 'components/UI/Typography'
import { useTheme } from 'contexts/theme-context'
import { useAppSelector } from 'hooks'
import { Fragment, useMemo, useState } from 'react'
import Confetti from 'react-confetti'
import { Trans, useTranslation } from 'react-i18next'
import { getCreditParamsSelector, getStatusSelector } from 'redux/store/slices/statusSlice'
import tw from 'twin.macro'
import { useWindowSize } from 'usehooks-ts'
import { numberWithSpaces } from 'utils'
import { conditionBlockStyles } from 'utils/constants'
import { getProgramName } from 'utils/translations'

import FreedomBox from './FreedomBox'

const Success = () => {
  const { isDarkTheme } = useTheme()
  const { width, height } = useWindowSize()
  const [isCondOpen, setIsCondOpen] = useState(false)
  const {
    t,
    i18n: { language }
  } = useTranslation()

  const { interest_rate, loan_amount, period } = useAppSelector(getCreditParamsSelector)
  const { loan_purpose, credit_program } = useAppSelector(getStatusSelector)

  const loanConditions = useMemo<Array<ConditionProps>>(
    () => [
      {
        caption: t('loan-conditions.loan-amount', { ns: 'common' }),
        body: `${numberWithSpaces(Number(loan_amount).toFixed(0))} ₸`,
        icon: <CashStackIcon />
      },
      {
        caption: t('loan.period', { ns: 'common' }),
        body: t('calculator.period-limit', { ns: 'common', period }),
        icon: <ClockIcon />
      },
      {
        caption: t('loan.interest-rate', { ns: 'common' }),
        body: `${interest_rate}%`,
        icon: <PercentageIcon />
      },
      {
        caption: t('programs.title', { ns: 'common' }),
        body: t(getProgramName(credit_program), { ns: 'common' }),
        icon: <StarIcon />
      }
    ],
    [credit_program, interest_rate, loan_amount, period, t]
  )
  return (
    <Fragment>
      <Confetti className='confetti bvi-no-styles' width={width} height={height} gravity={0.03} />
      <ContainerWithSteps>
        <div tw='px-4 pt-10 relative md:p-0'>
          <h1 tw='text-[22px] leading-[30px] font-semibold lg:text-[30px] lg:leading-10 lg:font-bold lg:max-w-[390px]'>
            {t('success-page.title')}
          </h1>
          {credit_program === 'DAMU' && loan_purpose === 'INVESTMENTS' && (
            <p tw='mt-5 max-w-[450px] text-[14px] tracking-[-0.24px] leading-[17px]'>
              <Trans
                components={{
                  // eslint-disable-next-line jsx-a11y/anchor-has-content
                  a: <a href='https://cabinet.bankffin.kz/' tw='font-medium text-[#2A8640] dark:text-[#4FB84E]' />
                }}
              >
                {t('success-page.confirm-goal')}
              </Trans>
            </p>
          )}
          <Typography
            variant='subbody'
            weight='bold'
            twStyle={tw`mt-5 text-base leading-5 font-medium text-[#2A8640] cursor-pointer dark:text-[#4FB84E]`}
            onClick={() => setIsCondOpen(true)}
          >
            {t('success-page.watch-conditions')}
          </Typography>
          <img
            tw='block mt-8 w-[200px] h-[190px] mx-auto lg:w-[285px] lg:h-[270px] xl:m-0 xl:absolute xl:top-0 xl:right-0 xl:translate-x-1/2'
            src={isDarkTheme ? IPImageDark : IPImageLight}
            alt='Business credit'
          />
          <div tw='my-8 xl:max-w-[332px] xl:mt-10 xl:mb-[4.5rem]'>
            <p tw='text-sm text-[#5C5C5C] dark:text-[#B7BBBD]'>{t('success-page.real-time-check')}</p>
            <div tw='mt-4 flex gap-x-3'>
              <a
                href={`https://bankffin.kz/${language}/res/mobile-app`}
                tw='py-3 flex flex-1 items-center justify-center rounded-xl border border-white bg-[#191919]'
              >
                <GooglePlay />
              </a>
              <a
                href={`https://bankffin.kz/${language}/res/mobile-app`}
                tw='py-3 flex flex-1 items-center justify-center rounded-xl border border-white bg-[#191919]'
              >
                <AppStore />
              </a>
            </div>
          </div>
          <FreedomBox />
        </div>
      </ContainerWithSteps>
      <Modal
        title={t('flow-loan-condition')}
        open={isCondOpen}
        onClose={() => setIsCondOpen(false)}
        twStyle={tw`max-w-[730px] w-full content-between p-0 pt-5`}
        headerStyle={tw`px-5 pb-5`}
        divider={true}
      >
        <Fragment>
          <div tw='grid grid-cols-2'>
            {loanConditions.map((item, id) => {
              return (
                <ConditionBlock
                  twStyle={conditionBlockStyles}
                  key={`condition_${id}`}
                  body={item.body}
                  caption={item.caption}
                  icon={item.icon}
                />
              )
            })}
          </div>
        </Fragment>
      </Modal>
    </Fragment>
  )
}

export default Success
